/**
 * lib/optionsAnalytics.ts — dealer flow + IV surface analytics.
 *
 * GEX / DEX / max pain / skew / term structure. Takes a flat OptionContract[]
 * (same shape served by /api/options) and slices it into the useful views.
 *
 * Convention for dealer flow (the standard one — Squeezemetrics, Spotgamma):
 *   - Calls are assumed sold by dealers (long gamma to dealers)
 *   - Puts are assumed bought by dealers (short gamma to dealers)
 *   - GEX per strike = (call_gamma × call_OI - put_gamma × put_OI) × 100 × spot²
 * The 100 multiplier is the contract size (100 shares per option). The
 * spot² scales gamma to dollar-PnL-per-1%-move for readability.
 *
 * Flip point is the strike where cumulative GEX crosses zero — below it
 * dealers are short gamma (volatility-amplifying), above it long gamma
 * (volatility-dampening). It's the level traders watch as the inflection.
 */

export interface OptionContract {
  symbol:       string;
  expiry:       string;
  strike:       number;
  type:         'call' | 'put';
  bid:          number;
  ask:          number;
  last:         number;
  iv:           number;
  ivRank:       number;
  delta:        number;
  gamma:        number;
  theta:        number;
  vega:         number;
  rho:          number;
  openInterest: number;
  volume:       number;
}

// ── GEX / DEX exposure ───────────────────────────────────────────────────

export interface ExposureRow {
  strike:    number;
  gex:       number;  // net dealer gamma exposure ($ per 1% move)
  dex:       number;  // net dealer delta exposure (shares)
  call_oi:   number;
  put_oi:    number;
}

export interface ExposureProfile {
  rows:       ExposureRow[];
  totalGEX:   number;
  totalDEX:   number;
  flipStrike: number | null;  // strike where cumulative GEX crosses zero
  maxGexStrike: number;       // strike with largest absolute GEX bar
}

/**
 * Net dealer GEX + DEX per strike, plus the cumulative-GEX flip point.
 * Pass the full chain for a single expiry and the underlying spot.
 */
export function exposureProfile(chain: OptionContract[], spot: number): ExposureProfile {
  const byStrike = new Map<number, { call?: OptionContract; put?: OptionContract }>();
  for (const c of chain) {
    if (!byStrike.has(c.strike)) byStrike.set(c.strike, {});
    byStrike.get(c.strike)![c.type] = c;
  }

  const rows: ExposureRow[] = [];
  for (const [strike, kv] of byStrike) {
    const cOi = kv.call?.openInterest ?? 0;
    const pOi = kv.put?.openInterest  ?? 0;
    const cG  = kv.call?.gamma ?? 0;
    const pG  = kv.put?.gamma  ?? 0;
    const cD  = kv.call?.delta ?? 0;
    const pD  = kv.put?.delta  ?? 0;
    // GEX in $ per 1% move. Dealer convention: long calls (short to dealer
    // = negative dealer gamma), short puts (long to dealer = positive
    // dealer gamma). We follow Squeezemetrics: net = call_gamma * call_OI
    // - put_gamma * put_OI, scaled to dollar terms.
    const gex = (cG * cOi - pG * pOi) * 100 * spot * spot * 0.01;
    // DEX in shares. Dealers offset their delta -> sign is reversed.
    const dex = -(cD * cOi + pD * pOi) * 100;
    rows.push({ strike, gex, dex, call_oi: cOi, put_oi: pOi });
  }
  rows.sort((a, b) => a.strike - b.strike);

  // Flip point: the strike at which cumulative GEX (walking up from the
  // lowest strike) crosses zero. Interpolated linearly between the two
  // bracketing strikes.
  let cum = 0;
  let flip: number | null = null;
  let prev = { strike: rows[0]?.strike ?? 0, cum: 0 };
  for (const r of rows) {
    const next = cum + r.gex;
    if (flip === null && prev.cum < 0 && next >= 0) {
      const frac = (0 - prev.cum) / (next - prev.cum);
      flip = prev.strike + frac * (r.strike - prev.strike);
    } else if (flip === null && prev.cum > 0 && next <= 0) {
      const frac = (prev.cum - 0) / (prev.cum - next);
      flip = prev.strike + frac * (r.strike - prev.strike);
    }
    cum = next;
    prev = { strike: r.strike, cum };
  }

  const maxAbs = rows.reduce((best, r) => Math.abs(r.gex) > Math.abs(best.gex) ? r : best, rows[0] ?? { strike: 0, gex: 0, dex: 0, call_oi: 0, put_oi: 0 });

  return {
    rows,
    totalGEX:     cum,
    totalDEX:     rows.reduce((s, r) => s + r.dex, 0),
    flipStrike:   flip,
    maxGexStrike: maxAbs.strike,
  };
}

// ── Max pain ─────────────────────────────────────────────────────────────

/**
 * Max pain — the strike at which the *total* dollar payout to option
 * holders is minimized at expiry. The level option writers (dealers)
 * would prefer the underlying to pin to. Often respected near expiry.
 */
export function maxPain(chain: OptionContract[]): number {
  // Build a unique strike list.
  const strikes = Array.from(new Set(chain.map(c => c.strike))).sort((a, b) => a - b);
  if (strikes.length === 0) return NaN;

  let best = { strike: strikes[0], payout: Infinity };
  for (const expiry of strikes) {
    let payout = 0;
    for (const c of chain) {
      if (c.type === 'call' && expiry > c.strike) {
        payout += (expiry - c.strike) * c.openInterest * 100;
      } else if (c.type === 'put' && expiry < c.strike) {
        payout += (c.strike - expiry) * c.openInterest * 100;
      }
    }
    if (payout < best.payout) best = { strike: expiry, payout };
  }
  return best.strike;
}

// ── IV skew ──────────────────────────────────────────────────────────────

export interface SkewPoint { strike: number; iv: number; type: 'call' | 'put' }

/**
 * IV skew curve for a single expiry. Returns ITM puts (low strikes) and
 * OTM calls (high strikes) so the resulting line shows the put-skew shape.
 * Each point keeps its type so the chart can color-code.
 */
export function ivSkew(chain: OptionContract[], spot: number): SkewPoint[] {
  // Use OTM puts (strike < spot) + OTM calls (strike > spot) since those
  // carry the most liquid IV signal (ATM options have very similar IVs
  // between calls and puts due to put-call parity).
  const pts: SkewPoint[] = [];
  for (const c of chain) {
    if (c.type === 'put'  && c.strike < spot) pts.push({ strike: c.strike, iv: c.iv, type: 'put'  });
    if (c.type === 'call' && c.strike > spot) pts.push({ strike: c.strike, iv: c.iv, type: 'call' });
  }
  return pts.sort((a, b) => a.strike - b.strike);
}

/** Numeric skew metric: 25Δ put IV minus 25Δ call IV. Positive = put-skewed. */
export function skewNumber(chain: OptionContract[]): number {
  const otmPut  = chain.filter(c => c.type === 'put') .reduce(
    (best, c) => Math.abs(c.delta + 0.25) < Math.abs((best?.delta ?? -1) + 0.25) ? c : best,
    chain.find(c => c.type === 'put'),
  );
  const otmCall = chain.filter(c => c.type === 'call').reduce(
    (best, c) => Math.abs(c.delta - 0.25) < Math.abs((best?.delta ?? 1) - 0.25) ? c : best,
    chain.find(c => c.type === 'call'),
  );
  if (!otmPut || !otmCall) return 0;
  return otmPut.iv - otmCall.iv;
}

// ── IV term structure ────────────────────────────────────────────────────

export interface TermPoint { expiry: string; dte: number; atmIv: number }

/**
 * IV term structure — the ATM IV of each expiry plotted against DTE.
 * Upward-sloping = contango (normal). Downward = backwardation (often
 * around earnings or a stress event). Pass a multi-expiry chain.
 */
export function termStructure(chain: OptionContract[], spot: number): TermPoint[] {
  const byExpiry = new Map<string, OptionContract[]>();
  for (const c of chain) {
    if (!byExpiry.has(c.expiry)) byExpiry.set(c.expiry, []);
    byExpiry.get(c.expiry)!.push(c);
  }
  const today = Date.now();
  const out: TermPoint[] = [];
  for (const [expiry, opts] of byExpiry) {
    // ATM = strike closest to spot, blend call+put IV
    const atmCall = opts.filter(o => o.type === 'call')
      .reduce<OptionContract | null>((best, o) => !best || Math.abs(o.strike - spot) < Math.abs(best.strike - spot) ? o : best, null);
    const atmPut  = opts.filter(o => o.type === 'put')
      .reduce<OptionContract | null>((best, o) => !best || Math.abs(o.strike - spot) < Math.abs(best.strike - spot) ? o : best, null);
    if (!atmCall && !atmPut) continue;
    // Unknown ATM IV is NaN, not 0: a 0% implied vol is a real (and impossible)
  // reading that downstream skew ratios would divide by.
  const iv = (atmCall && atmPut) ? (atmCall.iv + atmPut.iv) / 2 : (atmCall?.iv ?? atmPut?.iv ?? NaN);
    const dte = Math.max(1, Math.round((new Date(expiry).getTime() - today) / 86400_000));
    out.push({ expiry, dte, atmIv: iv });
  }
  return out.sort((a, b) => a.dte - b.dte);
}
